(() => {
  'use strict';

  const DESKTOP_BREAKPOINT = 769;

  const initMainNavigation = (root = document) => {
    root.querySelectorAll('.cc-header-desktop .cc-nav-details').forEach((details) => {
      if (details.dataset.ccNavReady === 'true') return;
      details.dataset.ccNavReady = 'true';

      const summary = details.querySelector(':scope > summary');
      const dropdown = details.querySelector(':scope > .cc-megamenu, :scope > .cc-dropdown');
      if (!summary || !dropdown) return;

      let closeTimer = null;

      const open = () => {
        window.clearTimeout(closeTimer);
        document.querySelectorAll('.cc-header-desktop .cc-nav-details.is-cc-open').forEach((other) => {
          if (other !== details) {
            other.classList.remove('is-cc-open');
            other.removeAttribute('open');
            other.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
          }
        });
        details.classList.add('is-cc-open');
        details.setAttribute('open', '');
        summary.setAttribute('aria-expanded', 'true');
      };

      const close = () => {
        window.clearTimeout(closeTimer);
        details.classList.remove('is-cc-open');
        details.removeAttribute('open');
        summary.setAttribute('aria-expanded', 'false');
      };

      const scheduleClose = () => {
        window.clearTimeout(closeTimer);
        closeTimer = window.setTimeout(close, 480);
      };

      const hoverScope = details.closest('.cc-main-nav__item') || details;
      hoverScope.addEventListener('mouseenter', open);
      hoverScope.addEventListener('mouseleave', scheduleClose);
      summary.addEventListener('focus', open);
      dropdown.addEventListener('mouseenter', open);
      dropdown.addEventListener('mouseleave', scheduleClose);

      details.addEventListener('focusout', (event) => {
        if (!details.contains(event.relatedTarget)) scheduleClose();
      });

      summary.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (details.classList.contains('is-cc-open')) close();
        else open();
      });
    });

    if (document.documentElement.dataset.ccNavGlobalReady !== 'true') {
      document.documentElement.dataset.ccNavGlobalReady = 'true';
      document.addEventListener('click', (event) => {
        if (event.target.closest('.cc-header-desktop .cc-nav-details')) return;
        document.querySelectorAll('.cc-header-desktop .cc-nav-details.is-cc-open').forEach((details) => {
          details.classList.remove('is-cc-open');
          details.removeAttribute('open');
          details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
        });
      });
      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        document.querySelectorAll('.cc-header-desktop .cc-nav-details.is-cc-open').forEach((details) => {
          details.classList.remove('is-cc-open');
          details.removeAttribute('open');
          details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
        });
      });
    }
  };

  const initSlider = (slider) => {
    if (!slider || slider.dataset.ccSliderReady === 'true') return;

    const track = slider.querySelector('[data-cc-slider-track]');
    const previousButton = slider.querySelector('[data-cc-slider-prev]');
    const nextButton = slider.querySelector('[data-cc-slider-next]');
    const submenu = slider.querySelector('[data-cc-tcg-submenu]');
    const shell = slider.querySelector('.cc-tcg-slider__shell');
    if (!track || !previousButton || !nextButton || !submenu || !shell) return;

    slider.dataset.ccSliderReady = 'true';
    let activeTrigger = null;

    const closeSubmenu = ({ restoreFocus = false } = {}) => {
      const trigger = activeTrigger;
      submenu.hidden = true;
      submenu.innerHTML = '';
      submenu.style.removeProperty('left');
      slider.querySelectorAll('[data-cc-submenu-trigger][aria-expanded="true"]').forEach((button) => {
        button.setAttribute('aria-expanded', 'false');
        button.closest('[data-cc-tcg-item]')?.classList.remove('is-open');
      });
      activeTrigger = null;
      if (restoreFocus && trigger) trigger.focus();
    };

    const positionSubmenu = (trigger) => {
      if (!trigger || submenu.hidden) return;
      const item = trigger.closest('[data-cc-tcg-item]');
      if (!item) return;
      const itemRect = item.getBoundingClientRect();
      const shellRect = shell.getBoundingClientRect();
      const submenuWidth = Math.min(430, Math.max(300, submenu.offsetWidth));
      let left = itemRect.left - shellRect.left + itemRect.width / 2 - submenuWidth / 2;
      left = Math.max(0, Math.min(left, shellRect.width - submenuWidth));
      submenu.style.left = `${Math.round(left)}px`;
    };

    const openSubmenu = (trigger) => {
      const item = trigger.closest('[data-cc-tcg-item]');
      const template = item?.querySelector('[data-cc-submenu-template]');
      if (!item || !template) return;
      if (activeTrigger === trigger && trigger.getAttribute('aria-expanded') === 'true') {
        closeSubmenu({ restoreFocus: true });
        return;
      }
      closeSubmenu();
      activeTrigger = trigger;
      trigger.setAttribute('aria-expanded', 'true');
      item.classList.add('is-open');
      submenu.innerHTML = template.innerHTML;
      submenu.hidden = false;
      requestAnimationFrame(() => positionSubmenu(trigger));
    };

    const updateArrows = () => {
      const maximum = Math.max(0, track.scrollWidth - track.clientWidth);
      const overflowing = maximum > 4;
      slider.classList.toggle('is-overflowing', overflowing);
      previousButton.disabled = !overflowing || track.scrollLeft <= 4;
      nextButton.disabled = !overflowing || track.scrollLeft >= maximum - 4;
    };

    const getScrollStep = () => {
      const first = track.querySelector('[data-cc-tcg-item]');
      if (!first) return Math.max(320, Math.round(track.clientWidth * 0.7));
      const second = first.nextElementSibling;
      const firstRect = first.getBoundingClientRect();
      if (!second) return Math.round(firstRect.width * 2);
      const secondRect = second.getBoundingClientRect();
      return Math.round((firstRect.width + Math.max(0, secondRect.left - firstRect.right)) * 3);
    };

    slider.querySelectorAll('[data-cc-submenu-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        openSubmenu(trigger);
      });
    });

    previousButton.addEventListener('click', () => {
      closeSubmenu();
      track.scrollBy({ left: -getScrollStep(), behavior: 'smooth' });
    });
    nextButton.addEventListener('click', () => {
      closeSubmenu();
      track.scrollBy({ left: getScrollStep(), behavior: 'smooth' });
    });
    track.addEventListener('scroll', () => {
      updateArrows();
      if (activeTrigger) positionSubmenu(activeTrigger);
    }, { passive: true });
    document.addEventListener('click', (event) => {
      if (!slider.contains(event.target)) closeSubmenu();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && activeTrigger) closeSubmenu({ restoreFocus: true });
    });

    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(() => {
        updateArrows();
        if (activeTrigger) positionSubmenu(activeTrigger);
      });
      observer.observe(track);
      observer.observe(shell);
    }
    updateArrows();
  };

  const initSearchOverlay = () => {
    /*
     * MOBILE:
     * Die originale Impulse-Suche darf zuerst selbst reagieren.
     * Falls sie den Klick zwar abfängt, aber keinen sichtbaren Suchbereich öffnet,
     * wird nach kurzer Prüfung sauber auf die normale Shopify-Suchseite gewechselt.
     * Dadurch wird ausschließlich die mobile Suchfunktion abgesichert.
     */
    if (document.documentElement.dataset.ccMobileSearchReady !== 'true') {
      document.documentElement.dataset.ccMobileSearchReady = 'true';

      document.querySelectorAll('.js-search-header').forEach((trigger) => {
        trigger.addEventListener('click', () => {
          if (window.innerWidth >= DESKTOP_BREAKPOINT) return;

          const searchUrl = trigger.getAttribute('href');
          if (!searchUrl) return;

          window.setTimeout(() => {
            const visibleSearch = Array.from(
              document.querySelectorAll(
                '.site-header__search-container, .site-header__search, [role="search"], input[type="search"]'
              )
            ).some((element) => {
              const style = window.getComputedStyle(element);
              const rect = element.getBoundingClientRect();

              return (
                style.display !== 'none' &&
                style.visibility !== 'hidden' &&
                Number(style.opacity) !== 0 &&
                rect.width > 0 &&
                rect.height > 0
              );
            });

            if (!visibleSearch) {
              window.location.assign(searchUrl);
            }
          }, 180);
        }, true);
      });
    }

    /*
     * DESKTOP:
     * Bestehende CrispyCards-Suchlogik unverändert beibehalten.
     */
    const container = document.querySelector('.cc-header-desktop .site-header__search-container');
    if (!container || container.dataset.ccSearchReady === 'true') return;

    const panel = container.querySelector('.site-header__search');
    const triggers = document.querySelectorAll('.cc-header-desktop .js-search-header');
    if (!panel || !triggers.length) return;

    container.dataset.ccSearchReady = 'true';
    container.setAttribute('aria-hidden', 'true');

    let closeButton = panel.querySelector('.cc-search-close');
    if (!closeButton) {
      closeButton = document.createElement('button');
      closeButton.type = 'button';
      closeButton.className = 'cc-search-close';
      closeButton.setAttribute('aria-label', 'Suche schließen');
      closeButton.innerHTML = '&times;';
      panel.appendChild(closeButton);
    }

    const close = () => {
      document.body.classList.remove('cc-search-open');
      container.classList.remove('cc-search-open');
      container.setAttribute('aria-hidden', 'true');
    };

    const open = (event) => {
      if (window.innerWidth < DESKTOP_BREAKPOINT) return;

      event?.preventDefault();
      event?.stopImmediatePropagation();

      document.body.classList.add('cc-search-open');
      container.classList.add('cc-search-open');
      container.setAttribute('aria-hidden', 'false');

      window.setTimeout(() => {
        container.querySelector('input[type="search"], input[type="text"]')?.focus();
      }, 50);
    };

    triggers.forEach((trigger) => trigger.addEventListener('click', open, true));
    closeButton.addEventListener('click', close, true);

    container.addEventListener('click', (event) => {
      if (!panel.contains(event.target)) close();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && document.body.classList.contains('cc-search-open')) close();
    });
  };

  const initCartLayer = () => {
    if (document.documentElement.dataset.ccCartReady === 'true') return;
    document.documentElement.dataset.ccCartReady = 'true';

    const drawerSelectors = '#CartDrawer, [data-drawer-id="CartDrawer"], .ajaxcart__drawer, .cart-drawer, .cart-sidebar, .drawer--right';
    const triggerSelectors = '.js-drawer-open-cart, [data-cart-toggle], [aria-controls*="CartDrawer"], a[href$="/cart"], a[href*="/cart?"], .site-nav__link--icon[href*="cart"]';
    const closeSelectors = '.js-drawer-close, .drawer__close, [data-drawer-close], [aria-label*="close" i], [aria-label*="schließ" i]';

    let openingLockUntil = 0;

    const isVisible = (element) => {
      if (!element) return false;
      const style = window.getComputedStyle(element);
      if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
      const rect = element.getBoundingClientRect();
      return rect.width > 240 && rect.height > 160 && rect.right > window.innerWidth * .55;
    };

    const setOpen = (open) => {
      document.documentElement.classList.toggle('cc-cart-open', open);
      document.body.classList.toggle('cc-cart-open', open);
    };

    const drawerIsOpen = () => {
      const classOpen = document.documentElement.classList.contains('js-drawer-open-right') ||
        document.body.classList.contains('js-drawer-open-right') ||
        document.documentElement.classList.contains('drawer-open-right') ||
        document.body.classList.contains('drawer-open-right');

      const elementOpen = Array.from(document.querySelectorAll(drawerSelectors)).some((drawer) =>
        drawer.classList.contains('drawer--is-open') ||
        drawer.classList.contains('is-open') ||
        drawer.getAttribute('aria-hidden') === 'false' ||
        isVisible(drawer)
      );

      return classOpen || elementOpen;
    };

    const sync = () => {
      const detectedOpen = drawerIsOpen();
      if (!detectedOpen && performance.now() < openingLockUntil) return;
      setOpen(detectedOpen);
    };

    document.addEventListener('click', (event) => {
      if (event.target.closest(triggerSelectors)) {
        openingLockUntil = performance.now() + 1200;
        setOpen(true);
        window.setTimeout(sync, 180);
        window.setTimeout(sync, 450);
        window.setTimeout(sync, 900);
        window.setTimeout(sync, 1300);
      }

      if (event.target.closest(closeSelectors)) {
        openingLockUntil = 0;
        window.setTimeout(() => setOpen(false), 260);
        window.setTimeout(sync, 420);
      }
    }, true);

    if ('MutationObserver' in window) {
      const observer = new MutationObserver(() => window.requestAnimationFrame(sync));
      observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
      observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

      const observeDrawers = () => {
        document.querySelectorAll(drawerSelectors).forEach((drawer) => {
          if (drawer.dataset.ccCartObserved === 'true') return;
          drawer.dataset.ccCartObserved = 'true';
          observer.observe(drawer, { attributes: true, attributeFilter: ['class', 'style', 'aria-hidden'] });
        });
      };

      observeDrawers();
      new MutationObserver(observeDrawers).observe(document.body, { childList: true, subtree: true });
    }

    sync();
  };

  const clearDrawerTransforms = () => {
    if (window.innerWidth > 768) return;
    const properties = ['transform', 'translate', 'left', 'right', 'width', 'max-width', 'min-width', 'margin-left', 'margin-right'];
    document.querySelectorAll('html, body, #PageContainer, .page-container, .transition-body, .main-content, #MainContent').forEach((element) => {
      properties.forEach((property) => element.style.removeProperty(property));
    });
  };

  const initAll = (root = document) => {
    initMainNavigation(root);
    root.querySelectorAll('[data-cc-tcg-slider]').forEach(initSlider);
    initSearchOverlay();
    initCartLayer();
    clearDrawerTransforms();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  window.addEventListener('resize', clearDrawerTransforms, { passive: true });
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
